import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../../supabase";
import GalleryCard from "./GalleryCard";
import { useHistory, useLocation } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import { useTranslation } from "react-i18next";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";
const PAGE_SIZE = 25;

/* 쿠키커터 갤러리 목록 (2026-10-16 리뉴얼, 김현비 확정 시안 mock2#list — 카테고리 없음) */
export default function GalleryList() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { user } = useAuth();

  const tag = new URLSearchParams(location.search).get("tag") || "";

  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [sort, setSort] = useState("latest");

  const reqIdRef = useRef(0);

  useEffect(() => {
    const q = search.trim();
    if (!q) {
      setSuggestions([]);
      return;
    }

    const controller = new AbortController();

    fetch(
      `${API_BASE}/search/suggest?q=${encodeURIComponent(q)}`,
      { signal: controller.signal }
    )
      .then(res => res.json())
      .then(data => {
        setSuggestions(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        if (err.name !== "AbortError") console.error(err);
      });

    return () => controller.abort();
  }, [search]);

  // 검색어·정렬·태그가 바뀌면 처음부터
  useEffect(() => {
    setPage(1);
  }, [search, sort, tag]);

  useEffect(() => {
    fetchGallery(page);
  }, [page, search, sort, tag]);

  async function fetchGallery(targetPage) {
    const reqId = ++reqIdRef.current;
    const append = targetPage > 1;
    const from = (targetPage - 1) * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;
    const q = search.trim();

    setLoading(true);

    if (sort === "downloads") {
      // 인기순: 전체 기간 다운로드 순 RPC (기간 선택은 2026-10-16 에 뺐다)
      const { data, error } = await supabase.rpc("get_gallery_by_downloads", {
        period_start: null,
      });
      if (reqId !== reqIdRef.current) return;

      if (!error && data) {
        const visible = data.filter(
          (post) => post.status !== "hidden" && post.status !== "deleted"
        );
        const qLower = q.toLowerCase();
        const filtered = visible.filter((post) => {
          if (tag && !(post.tags || []).includes(tag)) return false;
          if (!q) return true;
          return (
            post.title?.toLowerCase().includes(qLower) ||
            post.tags?.some((tg) => tg.toLowerCase().includes(qLower))
          );
        });

        setTotal(filtered.length);
        const slice = filtered.slice(from, to + 1);
        setPosts((prev) => (append ? [...prev, ...slice] : slice));
      }
      setLoading(false);
      return;
    }

    let query = supabase
      .from("gallery")
      .select(`
    *,
    profiles (
      username
    )
  `, { count: "exact" })
      .eq("status", "approved")
      .order("created_at", { ascending: false });

    if (tag) query = query.contains("tags", [tag]);

    if (q) {
      query = query.or(`title.ilike.%${q}%,tags.cs.{${q}}`);
    }

    const { data, error, count } = await query.range(from, to);
    if (reqId !== reqIdRef.current) return;

    if (!error) {
      const rows = data || [];
      setPosts((prev) => (append ? [...prev, ...rows] : rows));
      setTotal(count || 0);
    }
    setLoading(false);
  }

  // 불러온 목록에서 다운로드 1회 이상 중 상위 3개에 "인기" 배지
  const popularIds = useMemo(() => {
    return new Set(
      posts
        .filter((p) => (p.download_count || 0) > 0)
        .sort((a, b) => (b.download_count || 0) - (a.download_count || 0))
        .slice(0, 3)
        .map((p) => p.id)
    );
  }, [posts]);

  const hasMore = posts.length < total;

  function onUploadClick() {
    history.push(user?.id ? "/gallery/write" : "/login");
  }

  return (
    <div className="gl-page">
      <div className="gl-wrap">
        {/* ===== Hero ===== */}
        <section className="gl-hero">
          <div>
            <small className="gl-pill">{t("galleryUi.heroPill")}</small>
            <h1>{t("galleryUi.title")}</h1>
            <p>{t("galleryUi.sub")}</p>
          </div>
          <div className="gl-up">
            <button type="button" className="gl-btn dark" onClick={onUploadClick}>
              {t("galleryUi.uploadBtn")}
            </button>
            {!user?.id && <small>{t("galleryUi.uploadNote")}</small>}
          </div>
        </section>

        {/* ===== 검색 + 정렬 ===== */}
        <div className="gl-bar">
          <div className={`gl-search ${suggestions.length ? "open" : ""}`}>
            <div className="gl-search-row">
              <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M21 21l-5-5" /></svg>
              <input
                type="text"
                value={search}
                placeholder={t("galleryUi.searchPlaceholder")}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearch(value);
                  if (!value.trim()) setSuggestions([]);
                }}
              />
            </div>

            {suggestions.length > 0 && (
              <ul className="gl-suggest">
                {suggestions.map((kw) => (
                  <li
                    key={kw}
                    onClick={() => {
                      setSearch(kw);
                      setSuggestions([]);
                    }}
                  >
                    <span className="dot" />
                    {kw}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="gl-seg" role="tablist">
            <button
              type="button"
              className={sort === "latest" ? "on" : ""}
              onClick={() => setSort("latest")}
            >
              {t("galleryUi.sortLatest")}
            </button>
            <button
              type="button"
              className={sort === "downloads" ? "on" : ""}
              onClick={() => setSort("downloads")}
            >
              {t("galleryUi.sortPopular")}
            </button>
          </div>
        </div>

        {tag && (
          <div className="gl-tagfilter">
            <button
              type="button"
              className="gl-tagchip"
              onClick={() => history.push("/gallery")}
              aria-label={t("galleryUi.removeTag")}
              title={t("galleryUi.removeTag")}
            >
              #{tag} <span>×</span>
            </button>
          </div>
        )}

        <div className="gl-cnt">
          {t("galleryUi.countLabel")} <b>{t("galleryUi.countUnit", { count: total })}</b>
        </div>

        {/* ===== Grid ===== */}
        {posts.length > 0 ? (
          <div className="gl-grid">
            {posts.map((post) => (
              <GalleryCard key={post.id} post={post} popular={popularIds.has(post.id)} />
            ))}
          </div>
        ) : (
          !loading && <div className="gl-empty">{t("gallery.empty")}</div>
        )}

        {hasMore && (
          <div className="gl-more">
            <button
              type="button"
              className="gl-btn ol"
              disabled={loading}
              onClick={() => setPage((p) => p + 1)}
            >
              {t("galleryUi.more")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
